import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { ArrowNarrowLeftIcon } from '@heroicons/react/outline';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import {
  Feature,
  MailSequenceCustomReferenceDateInput,
  MailSequenceClientPreview,
  MailSequenceReferenceDateType,
  FollowUpTemplateDataFragment,
  PatientDataFragment,
  PatientDataPermission,
  PatientLifecycleStatusV2,
  PatientNameDataFragment,
  ProgramTagDataFragment,
  ProgramTagWithCountDataFragment,
  useMailSequenceEnrollMutation,
  useMailSequenceTemplatesQuery,
  useMailSequencePreviewDatesMutation,
  useFollowUpTemplatesForPickerQuery,
  useMeProviderProgramTagsWithCountQuery,
  usePatientLazyQuery,
  usePatientNamesForRecipientsQuery,
  usePatientsV2NamesQuery,
} from '../../../generated/graphql';

import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagContext';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import InputGroup from '../../components/InputGroup';
import InputLabel from '../../components/InputLabel';
import IconButton from '../../components/IconButton';
import Spinner from '../../svgs/Spinner';
import { customToast } from '../../components/ToastAlert/customToast';
import { FollowUpForm, SavedActivityInputPreview } from '../../lib/followUp';
import { formatProviderActivityForFollowUp } from '../../lib/providerActivity';
import { getScheduleMailSendTimeLabel } from '../../lib/time';
import { sortPatientsByAlphabeticalAscending } from '../../components/PatientItem';
import useFixPage from '../../hooks/useFixPage';
import usePaintScreen from '../../hooks/usePaintScreen';
import {
  DEFAULT_TEMPLATE_REFERENCE_DATE,
  DEFAULT_SEND_TIME,
  MAX_OFFSET_DAYS,
  MailSequenceTemplate,
  TemplateStepDraft,
  REFERENCE_DATE_OPTIONS,
  StepActivitiesState,
  StepResourceModeState,
  buildEnrollStepActivitiesFromState,
  buildStepValidationErrors,
  isValidSequenceStepOffset,
  migrateStepActivitySelection,
  normalizeTimeString,
  referenceDateLabel,
} from '../../lib/mailSequence';
import { useSequenceStepsState } from '../../hooks/useSequenceStepsState';
import RecipientSelector from '../FollowUpBuilder/RecipientSelector';
import {
  getEnrollableRecipientPatients,
  PatientSelectionEntry,
} from '../FollowUpBuilder/hooks/useRecipientSelection';
import SequenceStepEditor from './SequenceStepEditor';
import SchedulePreviewModal from './SchedulePreviewModal';
import StepResourcePanel from './StepResourcePanel';
import AnchorDateSummary from './AnchorDateSummary';
import BuilderStepHeading from './BuilderStepHeading';
import SaveTemplateModal from './SaveTemplateModal';
import ApplyTemplateDrawer from './ApplyTemplateDrawer';
import IllustrationMail from '../../svgs/IllustrationMail';
import {
  anchorDateFieldForReferenceType,
  formatMailScheduleSummary,
  getResolvedAnchorDateIso,
  isProgramDateSet,
  summarizeAnchorDates,
} from './anchorDates';
import {
  getMailTimingIssuesForStep,
  hasMailTimingErrors,
  scheduleDateTypeLabel,
  validateMailTiming,
} from './mailTimingValidation';
import MailAlertMessage, {
  MailAlertItem,
  parseMailPrefixedError,
  textSegment,
} from '../../components/MailAlertMessage';
import {
  snapshotActivitiesToSavedPreviews,
  toEnrollmentActivityInputs,
} from './helpers';
import {
  SCHEDULE_MAIL_PATH,
  ScheduleMailLocationState,
  isScheduleMailLocationState,
} from './types';
import {
  clearScheduleMailDraft,
  hasComposerPrefill,
  inferStepResourceModesFromLegacy,
  loadScheduleMailDraft,
  saveScheduleMailDraft,
} from './scheduleMailDraft';
import {
  collectUnsignedMultiSigActivities,
  SignerActivityLocator,
} from './multiSigSigners';
import { MAIL_SEQUENCE_ALERT_LINK_CLASS } from './scheduleMailFieldLabel';
import ReviewSignerDetailsModal, {
  SavedActivityInputPreviewWithActivityIndex,
} from '../../components/Modals/MultiSignatureModals/ReviewSignerDetailsModal';
import { MULTI_FORM_FETCH_DELAY_MS } from '../../lib/constants';
import { logger } from '../../../lib/logger';

type PatientsOrTags = (
  | PatientNameDataFragment
  | ProgramTagWithCountDataFragment
  | ProgramTagDataFragment
)[];

const getInitialPageState = (
  locationState: ScheduleMailLocationState | null,
  prefilledSubject: string,
  prefilledProviderNote: string,
) => {
  const fromComposer = hasComposerPrefill(locationState);
  const draft = fromComposer ? null : loadScheduleMailDraft();
  const stepActivitiesState =
    draft?.stepActivitiesState ??
    migrateStepActivitySelection(
      (draft as { stepActivitySelection?: Record<number, number[] | null> })
        ?.stepActivitySelection,
      (draft?.activities ?? []) as FollowUpForm['activities'],
    );

  return {
    fromComposer,
    patientsOrTags: (locationState?.recipientPatients ??
      draft?.patientsOrTags ??
      []) as PatientsOrTags,
    activities: (locationState?.activities ??
      draft?.activities ??
      []) as FollowUpForm['activities'],
    referenceDateType:
      locationState?.referenceDateType ??
      draft?.referenceDateType ??
      DEFAULT_TEMPLATE_REFERENCE_DATE,
    defaultSendTime: draft?.defaultSendTime ?? DEFAULT_SEND_TIME,
    templateId: draft?.templateId ?? '',
    sequenceName: draft?.sequenceName ?? '',
    steps:
      draft?.steps ??
      ([
        {
          sortOrder: 0,
          offsetDays: 30,
          subject: prefilledSubject,
          providerNote: prefilledProviderNote,
        },
      ] as TemplateStepDraft[]),
    customAnchorDate:
      locationState?.customAnchorDate ?? draft?.customAnchorDate ?? '',
    stepActivitiesState,
    stepResourceModes:
      draft?.stepResourceModes ??
      inferStepResourceModesFromLegacy(stepActivitiesState),
    stepSourceTemplateIds: draft?.stepSourceTemplateIds ?? {},
    stepSourceTemplateNames: draft?.stepSourceTemplateNames ?? {},
  };
};

const ScheduleMail: FC = () => {
  useFixPage();
  const showPage = usePaintScreen();
  const navigate = useNavigate();
  const location = useLocation();
  const { authedProviderUser } = useAuth();
  const prefillAppliedRef = useRef(false);
  const draftRestoredRef = useRef(false);

  const locationState: ScheduleMailLocationState | null =
    isScheduleMailLocationState(location.state) ? location.state : null;

  const returnTo = locationState?.returnTo ?? '/home';
  const prefilledSubject = locationState?.subject ?? '';
  const prefilledProviderNote = locationState?.providerNote ?? '';
  const providerVoiceNoteId = locationState?.providerVoiceNoteId ?? null;
  const isIntake = locationState?.isIntake ?? false;

  const initialPageStateRef = useRef(
    getInitialPageState(locationState, prefilledSubject, prefilledProviderNote),
  );
  const initialPageState = initialPageStateRef.current;

  const [patientsOrTags, setPatientsOrTags] = useState<PatientsOrTags>(
    initialPageState.patientsOrTags,
  );
  const [patientSelections, setPatientSelections] = useState<
    Record<string, PatientSelectionEntry>
  >({});

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FollowUpForm>({
    defaultValues: {
      activities: initialPageState.activities,
      subject: '',
      patientsOrTags: [],
      note: '',
      providerVoiceNoteId: null,
    },
  });

  const { remove, update, prepend } = useFieldArray({
    control,
    name: 'activities',
  });

  useEffect(() => {
    if (prefillAppliedRef.current || !locationState) return;
    prefillAppliedRef.current = true;

    if (locationState.recipientPatients?.length) {
      setPatientsOrTags(locationState.recipientPatients);
    }
    if (locationState.activities?.length) {
      setValue('activities', locationState.activities);
    }
  }, [locationState, setValue]);

  const { data: meProviderProgramTagsData } =
    useMeProviderProgramTagsWithCountQuery();

  const providerProgramTags =
    meProviderProgramTagsData?.meProvider?.provider?.programTags ?? [];

  const { data: patientsData } = usePatientsV2NamesQuery({
    variables: {
      patientLifecycleStatuses: [
        PatientLifecycleStatusV2.Invited,
        PatientLifecycleStatusV2.Active,
      ],
      assignedPatients:
        authedProviderUser.patientDataPermission ===
        PatientDataPermission.Assigned,
    },
  });

  const patientsList = sortPatientsByAlphabeticalAscending(
    patientsData?.patientNames?.filter((patient) =>
      authedProviderUser?.hasPremiumAccess ? true : patient.isTestProgram,
    ),
  );

  const baseEnrollableRecipientPatients = useMemo(
    () => getEnrollableRecipientPatients(patientSelections),
    [patientSelections],
  );

  const programIds = useMemo(
    () =>
      Array.from(
        new Set(
          baseEnrollableRecipientPatients.map((p) => p.programInstanceId),
        ),
      ),
    [baseEnrollableRecipientPatients],
  );

  const { data: selectedRecipientsData } = usePatientNamesForRecipientsQuery({
    variables: { programIds },
    skip: programIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const enrollableRecipientPatients = useMemo(() => {
    const patientsByProgramId = new Map<string, PatientNameDataFragment>();
    (patientsList as PatientNameDataFragment[]).forEach((patient) => {
      patientsByProgramId.set(patient.programInstanceId, patient);
    });
    selectedRecipientsData?.patientNamesForRecipients?.forEach((patient) => {
      patientsByProgramId.set(patient.programInstanceId, patient);
    });
    return baseEnrollableRecipientPatients.map(
      (patient) =>
        patientsByProgramId.get(patient.programInstanceId) ?? patient,
    );
  }, [
    baseEnrollableRecipientPatients,
    patientsList,
    selectedRecipientsData?.patientNamesForRecipients,
  ]);

  const onlyPatient =
    enrollableRecipientPatients.length === 1
      ? enrollableRecipientPatients[0]
      : undefined;

  const [referenceDateType, setReferenceDateType] =
    useState<MailSequenceReferenceDateType>(initialPageState.referenceDateType);
  const [defaultSendTime, setDefaultSendTime] = useState<string>(
    initialPageState.defaultSendTime,
  );
  const [templateId, setTemplateId] = useState<string | ''>(
    initialPageState.templateId,
  );
  const [sequenceName, setSequenceName] = useState(
    initialPageState.sequenceName,
  );
  const [customAnchorDate, setCustomAnchorDate] = useState(
    initialPageState.customAnchorDate,
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitErrors, setSubmitErrors] = useState<MailAlertItem[]>([]);
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const {
    steps,
    setSteps,
    stepsRevision,
    bumpStepsRevision,
    stepActivitiesState,
    setStepActivitiesState,
    stepResourceModes,
    setStepResourceModes,
    stepSourceTemplateIds,
    setStepSourceTemplateIds,
    stepSourceTemplateNames,
    setStepSourceTemplateNames,
    setStepActivities,
    setStepResourceMode,
    updateStep,
    addStep,
    removeStep,
  } = useSequenceStepsState({
    initialSteps: initialPageState.steps,
    initialStepActivitiesState: initialPageState.stepActivitiesState,
    initialStepResourceModes: initialPageState.stepResourceModes,
    initialStepSourceTemplateIds: initialPageState.stepSourceTemplateIds,
    initialStepSourceTemplateNames: initialPageState.stepSourceTemplateNames,
    onMutate: () => setSubmitErrors([]),
  });
  const stepActivitiesStateRef = useRef(stepActivitiesState);
  const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);

  useEffect(() => {
    if (draftRestoredRef.current || initialPageState.fromComposer) return;
    draftRestoredRef.current = true;
    if (!loadScheduleMailDraft()) return;
    bumpStepsRevision();
  }, [initialPageState.fromComposer, bumpStepsRevision]);
  const [applyTemplateStepIndex, setApplyTemplateStepIndex] = useState<
    number | null
  >(null);
  const [isReviewSignerDetailsModalOpen, setIsReviewSignerDetailsModalOpen] =
    useState(false);
  const [signerModalActivities, setSignerModalActivities] = useState<
    SavedActivityInputPreviewWithActivityIndex[]
  >([]);
  const signerModalLocatorsRef = useRef<SignerActivityLocator[]>([]);
  const [patientsDataForModal, setPatientsDataForModal] = useState<
    PatientDataFragment[]
  >([]);
  const [fetchPatient] = usePatientLazyQuery();

  const refetchPatientForModal = useCallback(
    async (programId: string) => {
      try {
        const result = await fetchPatient({
          variables: { programId },
          fetchPolicy: 'network-only',
        });
        if (result.data?.patient) {
          setPatientsDataForModal((prev) => {
            const updated = [...prev];
            const index = updated.findIndex(
              (p) => p.programInstanceId === programId,
            );
            if (index >= 0) {
              updated[index] = result.data!.patient!;
            } else {
              updated.push(result.data!.patient!);
            }
            return updated;
          });
        }
      } catch (error) {
        logger.error(
          error instanceof Error
            ? error
            : new Error('Error refetching patient for signer modal'),
          { programId },
        );
        customToast.error(
          'Failed to refresh client information. Please try again.',
        );
      }
    },
    [fetchPatient],
  );

  const refetchPatient = useCallback(
    async (variables?: { programId?: string }) => {
      const programId = variables?.programId;
      if (!programId) {
        throw new Error('programId is required to refetch patient');
      }
      const result = await fetchPatient({
        variables: { programId },
        fetchPolicy: 'network-only',
      });
      if (result.data?.patient) {
        setPatientsDataForModal((prev) => {
          const updated = [...prev];
          const index = updated.findIndex(
            (p) => p.programInstanceId === programId,
          );
          if (index >= 0) {
            updated[index] = result.data!.patient!;
          }
          return updated;
        });
      }
      return result;
    },
    [fetchPatient],
  );

  // Live form values (useWatch returns fresh clones on change), unlike the
  // registration-time snapshot `fields` from useFieldArray — see A3.
  const watchedActivities = useWatch({ control, name: 'activities' });
  const defaultActivities = useMemo(
    () => (watchedActivities ?? []) as FollowUpForm['activities'],
    [watchedActivities],
  );

  useEffect(() => {
    if (!prefilledSubject && !prefilledProviderNote) return;
    if (!initialPageState.fromComposer && loadScheduleMailDraft()) return;
    setSteps((prev) =>
      prev.map((s, i) =>
        i === 0
          ? {
              ...s,
              subject: s.subject || prefilledSubject,
              providerNote: s.providerNote || prefilledProviderNote,
            }
          : s,
      ),
    );
  }, [prefilledProviderNote, prefilledSubject, initialPageState.fromComposer]);

  useEffect(() => {
    setSubmitErrors([]);
  }, [
    patientsOrTags,
    referenceDateType,
    customAnchorDate,
    defaultSendTime,
    templateId,
    sequenceName,
  ]);

  useEffect(() => {
    saveScheduleMailDraft({
      patientsOrTags,
      activities: defaultActivities,
      referenceDateType,
      defaultSendTime,
      templateId,
      sequenceName,
      steps,
      customAnchorDate,
      stepActivitiesState,
      stepResourceModes,
      stepSourceTemplateIds,
      stepSourceTemplateNames,
    });
  }, [
    defaultActivities,
    customAnchorDate,
    defaultSendTime,
    patientsOrTags,
    templateId,
    sequenceName,
    referenceDateType,
    stepActivitiesState,
    stepResourceModes,
    stepSourceTemplateIds,
    stepSourceTemplateNames,
    steps,
  ]);

  const {
    data: presetsData,
    loading: presetsLoading,
    refetch: refetchPresets,
  } = useMailSequenceTemplatesQuery({
    fetchPolicy: 'cache-and-network',
  });

  useFollowUpTemplatesForPickerQuery({
    variables: { input: { isDraft: false } },
    fetchPolicy: 'cache-first',
  });

  const presets = useMemo(
    () =>
      [...(presetsData?.mailSequenceTemplates ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [presetsData?.mailSequenceTemplates],
  );

  const providerTimezone = (
    authedProviderUser?.provider as { timezone?: string } | undefined
  )?.timezone;

  const sendTimeLabel = useMemo(
    () => getScheduleMailSendTimeLabel(providerTimezone),
    [providerTimezone],
  );

  /**
   * Hydrate the whole builder from a sequence template. Called imperatively
   * when the user picks a template in the dropdown — never reactively — so
   * draft restores and "Save as template" cannot clobber in-progress edits.
   */
  const applyTemplate = (preset: MailSequenceTemplate) => {
    setSteps(
      [...preset.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((s, idx) => ({
          sortOrder: idx,
          offsetDays: s.offsetDays,
          subject: s.subject || (idx === 0 ? prefilledSubject : ''),
          providerNote:
            s.providerNote || (idx === 0 ? prefilledProviderNote : ''),
        })),
    );

    if (preset.hasDefaultActivities && preset.defaultActivityInputs.length) {
      setValue(
        'activities',
        snapshotActivitiesToSavedPreviews(
          preset.defaultActivityInputs,
          preset.defaultActivities,
        ),
      );
    } else {
      setValue('activities', []);
    }

    const newStepState: StepActivitiesState = {};
    const newStepModes: StepResourceModeState = {};
    const newTemplateIds: Record<number, string> = {};
    preset.steps.forEach((step, i) => {
      if (step.hasActivityOverride) {
        newStepState[i] = step.activityInputs.length
          ? snapshotActivitiesToSavedPreviews(
              step.activityInputs,
              step.activities,
            )
          : [];
        newStepModes[i] = 'custom';
      }
      if (step.sourceFollowUpTemplateId) {
        newTemplateIds[i] = step.sourceFollowUpTemplateId;
      }
    });
    setStepActivitiesState(newStepState);
    setStepResourceModes(newStepModes);
    setStepSourceTemplateIds(newTemplateIds);
    setStepSourceTemplateNames({});
    setSequenceName(preset.name?.trim() ?? '');
    if (
      preset.referenceDateType ===
        MailSequenceReferenceDateType.ProgramStartDate ||
      preset.referenceDateType === MailSequenceReferenceDateType.ProgramEndDate
    ) {
      setReferenceDateType(preset.referenceDateType);
    }

    bumpStepsRevision();
  };

  const applyTemplateToStep = (
    stepIndex: number,
    template: FollowUpTemplateDataFragment,
  ) => {
    const formattedActivities = template.activityTemplates.map(
      (activityTemplate, idx) =>
        formatProviderActivityForFollowUp(
          activityTemplate.activity,
          idx + 1,
          activityTemplate.providerNote,
        ),
    );

    updateStep(stepIndex, {
      subject: template.subject,
      providerNote: template.providerNote,
    });

    if (stepIndex === 0) {
      setValue('activities', formattedActivities);
    } else {
      setStepResourceModes((prev) => ({ ...prev, [stepIndex]: 'custom' }));
      setStepActivitiesState((prev) => ({
        ...prev,
        [stepIndex]: formattedActivities,
      }));
    }

    setStepSourceTemplateIds((prev) => ({
      ...prev,
      [stepIndex]: template.id,
    }));
    setStepSourceTemplateNames((prev) => ({
      ...prev,
      [stepIndex]: template.name,
    }));
    bumpStepsRevision();
    customToast.success(`Template applied to Mail ${stepIndex + 1}.`);
  };

  const isCustom = referenceDateType === MailSequenceReferenceDateType.Custom;

  const anchorLabel = referenceDateLabel(referenceDateType).toLowerCase();

  const customReferenceDates = useMemo<
    MailSequenceCustomReferenceDateInput[]
  >(() => {
    if (!isCustom || !customAnchorDate) return [];
    return programIds.map((programId) => ({
      programId,
      referenceDate: customAnchorDate,
    }));
  }, [customAnchorDate, isCustom, programIds]);

  const [previewMutation, { data: previewData, loading: previewing }] =
    useMailSequencePreviewDatesMutation({
      onError: (err) =>
        customToast.error(err.message || 'Could not preview dates.'),
    });

  const [enrollMutation, { loading: enrolling }] =
    useMailSequenceEnrollMutation({
      onCompleted: ({ mailSequenceEnroll }) => {
        if (mailSequenceEnroll?.success) {
          clearScheduleMailDraft();
          const enrolledCount = mailSequenceEnroll.enrollments?.length ?? 0;
          customToast.success(
            enrolledCount === 1
              ? 'Mail sequence started.'
              : `${enrolledCount} mail sequences started.`,
          );
          if (enrollableRecipientPatients.length === 1) {
            const enrolled = mailSequenceEnroll.enrollments?.[0];
            const programInstanceId =
              enrollableRecipientPatients[0].programInstanceId;
            navigate(
              enrolled
                ? `/clients/${programInstanceId}/sequences/${enrolled.id}`
                : `/clients/${programInstanceId}/sequences`,
            );
          } else {
            navigate('/clients/active');
          }
        } else {
          setServerError(
            mailSequenceEnroll?.error || 'Could not enroll clients.',
          );
        }
      },
      onError: (err) =>
        setServerError(err.message || 'Could not enroll clients.'),
    });

  const mailTimingIssues = useMemo(
    () =>
      validateMailTiming({
        recipients: enrollableRecipientPatients,
        referenceDateType,
        customAnchorDate,
        steps,
        defaultSendTime,
        providerTimezone,
      }),
    [
      customAnchorDate,
      defaultSendTime,
      enrollableRecipientPatients,
      providerTimezone,
      referenceDateType,
      steps,
    ],
  );

  const getEnrollmentValidationErrors = (): MailAlertItem[] => {
    const errs: MailAlertItem[] = [];
    if (programIds.length === 0) {
      errs.push({
        key: 'recipients',
        segments: [textSegment('Pick at least one client recipient.')],
      });
    }
    errs.push(
      ...buildStepValidationErrors(
        steps,
        scheduleDateTypeLabel(referenceDateType, customAnchorDate),
      ),
    );
    if (isCustom && programIds.length > 0 && !customAnchorDate) {
      errs.push({
        key: 'custom-date',
        segments: [textSegment('Pick a date above before scheduling Mail.')],
      });
    }
    return errs;
  };

  const canEditSequence = useMemo(() => {
    if (programIds.length === 0) return false;
    if (isCustom) return Boolean(customAnchorDate);
    const field = anchorDateFieldForReferenceType(referenceDateType);
    if (!field) return false;
    return (
      enrollableRecipientPatients.length > 0 &&
      enrollableRecipientPatients.every((patient) =>
        isProgramDateSet(patient[field]),
      )
    );
  }, [
    customAnchorDate,
    enrollableRecipientPatients,
    isCustom,
    programIds.length,
    referenceDateType,
  ]);
  const canPreview =
    canEditSequence &&
    steps.length > 0 &&
    steps.every(
      (s) =>
        isValidSequenceStepOffset(s.offsetDays, MAX_OFFSET_DAYS) &&
        s.subject.trim().length > 0 &&
        s.providerNote.trim().length > 0,
    );

  const handlePreview = () => {
    if (!canPreview) return;
    previewMutation({
      variables: {
        input: {
          programIds,
          referenceDateType,
          steps: steps.map((s) => ({
            offsetDays: s.offsetDays,
            subject: s.subject.trim(),
            providerNote: s.providerNote,
          })),
          customReferenceDates: isCustom ? customReferenceDates : undefined,
        },
      },
    });
  };

  const openPreviewModal = () => {
    setPreviewModalOpen(true);
    handlePreview();
  };

  const scrollLeftColumnToTop = () => {
    leftColumnRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const executeEnroll = useCallback(() => {
    const currentActivities = getValues(
      'activities',
    ) as FollowUpForm['activities'];
    const currentStepActivitiesState = stepActivitiesStateRef.current;
    enrollMutation({
      variables: {
        input: {
          programIds,
          activities: toEnrollmentActivityInputs(currentActivities),
          referenceDateType,
          steps: steps.map((s, i) => {
            const stepActivities = buildEnrollStepActivitiesFromState(
              currentStepActivitiesState,
              stepResourceModes,
              i,
              currentActivities,
              toEnrollmentActivityInputs,
            );
            return {
              offsetDays: s.offsetDays,
              subject: s.subject.trim(),
              providerNote: s.providerNote,
              ...(stepActivities ? { activities: stepActivities } : {}),
            };
          }),
          defaultSendTime: normalizeTimeString(defaultSendTime),
          customReferenceDates: isCustom ? customReferenceDates : undefined,
          templateId: templateId || undefined,
          name: sequenceName.trim() || null,
          providerVoiceNoteId: providerVoiceNoteId || undefined,
          sendEmail: true,
          sendSms: false,
          isIntake,
        },
      },
    });
  }, [
    customReferenceDates,
    defaultSendTime,
    enrollMutation,
    getValues,
    isCustom,
    isIntake,
    programIds,
    providerVoiceNoteId,
    referenceDateType,
    sequenceName,
    stepResourceModes,
    steps,
    templateId,
  ]);

  const openSignerModalForEnroll = async () => {
    const { activities: unsigned, locators } =
      collectUnsignedMultiSigActivities(
        defaultActivities,
        stepActivitiesState,
        stepResourceModes,
        steps.length,
      );
    if (unsigned.length === 0) {
      executeEnroll();
      return;
    }

    signerModalLocatorsRef.current = locators;
    setSignerModalActivities(unsigned);

    const patients: PatientDataFragment[] = [];
    for (let index = 0; index < enrollableRecipientPatients.length; index++) {
      const patientName = enrollableRecipientPatients[index];
      if (index > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, MULTI_FORM_FETCH_DELAY_MS),
        );
      }
      try {
        const result = await fetchPatient({
          variables: { programId: patientName.programInstanceId },
          fetchPolicy: 'network-only',
        });
        const returnedPatient = result.data?.patient;
        if (
          returnedPatient &&
          returnedPatient.programInstanceId === patientName.programInstanceId
        ) {
          patients.push(returnedPatient);
        }
      } catch (error) {
        logger.error(
          error instanceof Error
            ? error
            : new Error('Error fetching patient for signer modal'),
          { programId: patientName.programInstanceId },
        );
      }
    }

    if (patients.length === 0) {
      customToast.error('Unable to fetch client data for signer assignment.');
      return;
    }

    setPatientsDataForModal(patients);
    setIsReviewSignerDetailsModalOpen(true);
  };

  const handleSignerActivityUpdate = (
    index: number,
    value: SavedActivityInputPreview,
  ) => {
    const locator = signerModalLocatorsRef.current[index];
    if (!locator) return;

    if (locator.source === 'default') {
      update(locator.localIndex, value);
    } else {
      setStepActivitiesState((prev) => {
        const list = [...(prev[locator.stepIndex] ?? [])];
        list[locator.localIndex] = value;
        const next = { ...prev, [locator.stepIndex]: list };
        stepActivitiesStateRef.current = next;
        return next;
      });
    }

    setSignerModalActivities((prev) =>
      prev.map((activity, i) =>
        i === index ? { ...value, activityIndex: index } : activity,
      ),
    );
  };

  useEffect(() => {
    stepActivitiesStateRef.current = stepActivitiesState;
  }, [stepActivitiesState]);

  const handleSubmit = () => {
    if (!canEditSequence || enrolling) return;
    setServerError(null);
    const enrollmentErrors = getEnrollmentValidationErrors();
    if (enrollmentErrors.length > 0) {
      setSubmitErrors(enrollmentErrors);
      scrollLeftColumnToTop();
      return;
    }
    // Timing issues already surface on each mail step — block enroll without a second alert.
    if (hasMailTimingErrors(mailTimingIssues)) {
      setSubmitErrors([]);
      return;
    }
    setSubmitErrors([]);
    void openSignerModalForEnroll();
  };

  const previewByProgramId = useMemo(() => {
    const map: Record<string, MailSequenceClientPreview> = {};
    for (const p of previewData?.mailSequencePreviewDates?.previews ?? []) {
      map[p.programId] = p;
    }
    return map;
  }, [previewData?.mailSequencePreviewDates?.previews]);

  const previewRecipients = useMemo(
    () =>
      enrollableRecipientPatients.map((p) => ({
        programInstanceId: p.programInstanceId,
        title: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email,
      })),
    [enrollableRecipientPatients],
  );

  const previewSelectedTags = useMemo(
    () =>
      patientsOrTags
        .filter(
          (
            item,
          ): item is ProgramTagWithCountDataFragment | ProgramTagDataFragment =>
            item.__typename === 'ProgramTag',
        )
        .map((tag) => {
          const clientCount = Object.values(patientSelections).filter(
            (entry) =>
              (entry.manual || entry.selectedTagIds.length > 0) &&
              entry.selectedTagIds.includes(tag.id),
          ).length;
          return {
            id: tag.id,
            name: tag.name,
            color: tag.color ?? '',
            clientCount,
          };
        }),
    [patientSelections, patientsOrTags],
  );

  const previewClientsWithoutTagsCount = useMemo(
    () =>
      Object.values(patientSelections).filter(
        (entry) => entry.manual && entry.selectedTagIds.length === 0,
      ).length,
    [patientSelections],
  );

  const hasPreviewData = Boolean(
    previewData?.mailSequencePreviewDates?.previews?.length,
  );

  const anchorDateSummary = useMemo(
    () => summarizeAnchorDates(enrollableRecipientPatients, referenceDateType),
    [enrollableRecipientPatients, referenceDateType],
  );

  const resolvedAnchorDateIso = useMemo(
    () =>
      getResolvedAnchorDateIso(
        referenceDateType,
        customAnchorDate,
        anchorDateSummary,
      ),
    [anchorDateSummary, customAnchorDate, referenceDateType],
  );

  const mailScheduleSummaries = useMemo(
    () =>
      steps.map((step) =>
        formatMailScheduleSummary(
          step.offsetDays,
          anchorLabel,
          resolvedAnchorDateIso,
        ),
      ),
    [anchorLabel, resolvedAnchorDateIso, steps],
  );

  return (
    <>
      <div
        className={classNames(
          'flex flex-col bg-white',
          showPage ? 'visible' : 'invisible',
          'h-[calc(100vh-var(--top-nav-height))]',
        )}
      >
        <div className="fixed z-40 flex h-14 w-full flex-row items-center justify-between border-b border-neutral-50 bg-white py-3 px-20">
          <div className="flex min-w-[400px] flex-row items-center justify-start">
            <IconButton
              IconComponent={ArrowNarrowLeftIcon}
              iconClassName="text-secondary-100 w-5"
              aria-label="Back"
              className="rounded-xl px-3 py-2 ring-0"
              onClick={() => navigate(returnTo)}
            >
              <div className="small-caption ml-2 font-bold text-secondary-100">
                Back
              </div>
            </IconButton>
          </div>
          <div className="font-serif text-subtitle-small text-green-150">
            New Mail sequence
          </div>
          <div className="flex min-w-[333px] flex-row items-center justify-end gap-3">
            {(enrolling || previewing) && (
              <Spinner className="h-5 w-5 text-neutral-125" />
            )}
            <Button
              title="Save as template"
              theme="secondary"
              size="small"
              className="px-4 py-1.5"
              onClick={() => setSaveTemplateModalOpen(true)}
            />
            <Button
              title={previewing ? 'Loading preview...' : 'Preview'}
              theme="secondary"
              size="small"
              className="px-4 py-1.5"
              disabled={!canPreview || previewing}
              onClick={openPreviewModal}
            />
            <Button
              title={
                enrolling
                  ? 'Enrolling...'
                  : programIds.length > 1
                  ? `Enroll ${programIds.length} clients`
                  : 'Enroll client'
              }
              size="small"
              className="px-6 py-1.5"
              disabled={!canEditSequence || enrolling}
              onClick={handleSubmit}
            />
          </div>
        </div>

        <div className="flex h-full flex-row overflow-hidden pt-14">
          {/* Left: numbered setup steps */}
          <div
            ref={leftColumnRef}
            className="flex w-1/3 flex-col overflow-y-auto border-r border-neutral-50 px-8 py-8 pb-16"
          >
            {submitErrors.length > 0 ? (
              <div className="mb-6">
                <Alert
                  level="error"
                  message="There are issues with this sequence:"
                >
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {submitErrors.map((item) => (
                      <li key={item.key}>
                        <MailAlertMessage segments={item.segments} />
                      </li>
                    ))}
                  </ul>
                </Alert>
              </div>
            ) : null}
            {serverError ? (
              <div className="mb-6">
                <Alert level="error" message="Could not enroll clients">
                  <p className="mt-1">
                    <MailAlertMessage
                      segments={parseMailPrefixedError(serverError)}
                    />
                  </p>
                </Alert>
              </div>
            ) : null}
            <BuilderStepHeading step={1} title="Recipients" />
            <RecipientSelector
              value={patientsOrTags}
              onChange={setPatientsOrTags}
              patientsList={patientsList as PatientNameDataFragment[]}
              providerProgramTags={providerProgramTags}
              onPatientSelectionsChange={setPatientSelections}
            />
            {providerVoiceNoteId && (
              <p className="-mt-4 mb-8 text-small-caption text-neutral-110">
                Voice note attached from composer
              </p>
            )}

            <BuilderStepHeading
              step={2}
              title="Sequence schedule"
              description="Each Mail goes out a set number of days before or after this date."
            />
            <div className="mb-8 space-y-5">
              <div>
                <InputLabel
                  label="Schedule mail relative to"
                  useNaturalLettering
                  containerClassName="mb-1"
                />
                <select
                  className="text-sm w-full rounded-md border border-neutral-100 bg-white px-3 py-2 text-body text-neutral-150 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
                  value={referenceDateType}
                  onChange={(e) =>
                    setReferenceDateType(
                      e.target.value as MailSequenceReferenceDateType,
                    )
                  }
                >
                  {REFERENCE_DATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {isCustom ? (
                  <div className="mt-3">
                    <InputGroup
                      type="date"
                      label="Select a date"
                      useNaturalLettering
                      inputSize="extra-small"
                      containerClassName="w-full"
                      value={customAnchorDate}
                      onChange={(e) => setCustomAnchorDate(e.target.value)}
                    />
                  </div>
                ) : (
                  anchorDateSummary && (
                    <div className="mt-3">
                      <AnchorDateSummary summary={anchorDateSummary} />
                    </div>
                  )
                )}
              </div>

              <div>
                <InputGroup
                  type="time"
                  label={sendTimeLabel}
                  useNaturalLettering
                  inputSize="extra-small"
                  containerClassName="w-full"
                  value={defaultSendTime}
                  onChange={(e) => setDefaultSendTime(e.target.value)}
                />
              </div>
            </div>

            <BuilderStepHeading
              step={3}
              title="Sequence builder"
              muted={!canEditSequence}
              description={
                canEditSequence
                  ? 'Select a template or build your own sequence to the right.\nClick Add Mail + to add another step to the sequence.'
                  : 'Choose recipients and set your sequence timing above to start building your sequence.'
              }
              actions={
                canEditSequence ? (
                  <Button
                    title="Add Mail +"
                    theme="primary"
                    size="small"
                    onClick={addStep}
                  />
                ) : undefined
              }
            />
            {canEditSequence ? (
              <div className="space-y-4">
                {presetsLoading ? null : presets.length === 0 ? (
                  <Alert level="info" message="No Sequence Templates Yet">
                    <p className="mt-1">
                      <a
                        href="/automations/templates/mail-sequence"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={MAIL_SEQUENCE_ALERT_LINK_CLASS}
                      >
                        Create one in Automations
                      </a>{' '}
                      to save and reuse a sequence.
                    </p>
                  </Alert>
                ) : (
                  <div>
                    <InputLabel
                      label="Template (optional)"
                      useNaturalLettering
                      containerClassName="mb-1"
                    />
                    <select
                      className="text-sm w-full rounded-md border border-neutral-100 bg-white px-3 py-2 text-body text-neutral-150 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
                      value={templateId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setTemplateId(id);
                        if (!id) return;
                        const preset = presets.find((p) => p.id === id);
                        if (preset) applyTemplate(preset);
                      }}
                      disabled={presetsLoading}
                    >
                      <option value="">Custom (no template)</option>
                      {presets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <InputGroup
                  type="text"
                  label="Sequence name (optional)"
                  useNaturalLettering
                  inputSize="extra-small"
                  containerClassName="w-full"
                  placeholder="e.g. Post-retreat check-ins"
                  value={sequenceName}
                  onChange={(e) => setSequenceName(e.target.value)}
                  maxLength={255}
                />
              </div>
            ) : null}
          </div>

          {/* Right: step cards */}
          <div className="flex w-2/3 flex-col overflow-hidden">
            {canEditSequence ? (
              <div className="flex-1 overflow-y-auto px-8 py-8 pb-16">
                <div className="space-y-4">
                  {steps.map((step, i) => (
                    <SequenceStepEditor
                      key={`${i}-${stepsRevision}`}
                      stepIndex={i}
                      step={step}
                      scheduleSummary={mailScheduleSummaries[i] ?? ''}
                      timingIssues={getMailTimingIssuesForStep(
                        mailTimingIssues,
                        i,
                      )}
                      editorKey={`schedule-step-${i}-${stepsRevision}`}
                      onlyPatientFirstName={onlyPatient?.firstName ?? undefined}
                      onChange={(patch) => updateStep(i, patch)}
                      onRemove={() => removeStep(i)}
                      showRemove={i !== 0}
                      onApplyTemplate={() => setApplyTemplateStepIndex(i)}
                      sourceTemplateName={stepSourceTemplateNames[i]}
                      footer={
                        <StepResourcePanel
                          stepIndex={i}
                          defaultActivities={defaultActivities}
                          stepActivitiesState={stepActivitiesState}
                          stepResourceModes={stepResourceModes}
                          onSetStepActivities={(stepActivities) =>
                            setStepActivities(i, stepActivities)
                          }
                          onSetStepResourceMode={(mode) =>
                            setStepResourceMode(i, mode)
                          }
                          register={register}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                          remove={remove}
                          update={update}
                          prepend={prepend}
                        />
                      }
                    />
                  ))}
                </div>

                <div className="mt-6 flex justify-center">
                  <Button
                    title="Add Mail +"
                    theme="primary"
                    size="small"
                    onClick={addStep}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-8">
                <IllustrationMail className="mb-4 h-16 w-16 text-secondary-100" />
                <p className="max-w-sm text-center text-body text-neutral-125">
                  Choose recipients and set your sequence timing to start
                  building your sequence.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <SchedulePreviewModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        loading={previewing}
        recipients={previewRecipients}
        selectedTags={previewSelectedTags}
        clientsWithoutTagsCount={previewClientsWithoutTagsCount}
        previewByProgramId={previewByProgramId}
        hasPreviewData={hasPreviewData}
        steps={steps}
        defaultActivities={defaultActivities}
        stepActivitiesState={stepActivitiesState}
        stepResourceModes={stepResourceModes}
        referenceDateType={referenceDateType}
        defaultSendTime={defaultSendTime}
        anchorLabel={anchorLabel}
      />

      <SaveTemplateModal
        isOpen={saveTemplateModalOpen}
        onClose={() => setSaveTemplateModalOpen(false)}
        templateId={templateId || undefined}
        initialName={
          templateId ? presets.find((p) => p.id === templateId)?.name ?? '' : ''
        }
        steps={steps}
        defaultActivities={defaultActivities}
        stepActivitiesState={stepActivitiesState}
        stepResourceModes={stepResourceModes}
        stepSourceTemplateIds={stepSourceTemplateIds}
        onSaved={(id) => {
          setTemplateId(id);
          refetchPresets();
        }}
      />

      {applyTemplateStepIndex !== null && (
        <ApplyTemplateDrawer
          isOpen={applyTemplateStepIndex !== null}
          onClose={() => setApplyTemplateStepIndex(null)}
          stepIndex={applyTemplateStepIndex}
          onApplyTemplate={(template) =>
            applyTemplateToStep(applyTemplateStepIndex, template)
          }
        />
      )}

      <ReviewSignerDetailsModal
        setClosed={() => setIsReviewSignerDetailsModalOpen(false)}
        isOpen={isReviewSignerDetailsModalOpen}
        sendingProviderUser={authedProviderUser}
        multiSignatureActivities={signerModalActivities}
        patients={
          patientsDataForModal.length > 0 ? patientsDataForModal : undefined
        }
        update={handleSignerActivityUpdate as typeof update}
        refetchPatient={refetchPatient}
        refetchPatientForModal={refetchPatientForModal}
        sendFollowUpWithCheck={async () => {
          executeEnroll();
        }}
        submitting={enrolling}
        confirmCompleteLabel="Confirm & Enroll"
      />

      {!showPage && <Spinner className="mx-auto mt-24" />}
    </>
  );
};

const ScheduleMailGate: FC = () => {
  const {
    enabledFeatures,
    loading: featuresLoading,
    error: featuresError,
    isFeatureEnabled,
  } = useFeatureFlags();
  const featuresLoaded = enabledFeatures !== undefined;

  if (featuresLoading && !featuresLoaded) {
    return null;
  }
  // Failed flag fetch or missing access → leave rather than a blank page.
  if (featuresError || !isFeatureEnabled(Feature.MailSequences)) {
    return <Navigate to="/home" replace />;
  }
  return <ScheduleMail />;
};

export default ScheduleMailGate;
export { SCHEDULE_MAIL_PATH };
